import { NavLink, useLocation } from "react-router-dom";
import {
  MARKETPLACE_SIDEBAR_GROUPS,
  type MarketplaceSidebarDestination,
} from "../../components/layout/nav";
import {
  availabilityPill,
  deliveryBadge,
} from "../../components/marketplace/MarketplaceSetupSections.helpers";
import { useMarketplaceConnectionAttentionCount } from "../../hooks/useMarketplaceConnectionAttentionCount";
import { useMarketplaceRequestCount } from "../../hooks/useMarketplaceRequestCount";
import { useMarketplaceSetup } from "../../stores/marketplaceSetup";

/**
 * Marketplace pageSidebar — passed as `pageSidebar` to ProLayout via
 * AuthedLayout in App.tsx for all active Marketplace routes.
 *
 * Three groups (plan 443 M2): Inbox (Requests, Connections), Find buyers
 * (Browse buyers, Your offers), Your listing (Standing offers, Profile), with
 * the delivery + availability status chips above them. Group names are the one
 * place small-caps labels are used. Hrefs and active-state matching are
 * unchanged from the flat list this replaced. A destination can still render
 * as a non-navigable placeholder via `disabled` on the nav manifest; none are
 * disabled today.
 *
 * Styling follows the TodayGroupSidebar / ProLayout aside idiom (ADR-034).
 */
export interface MarketplaceSidebarProps {
  /**
   * Count of open marketplace connection requests — renders a small badge
   * next to the "Requests" destination when > 0. Overrides the live count
   * from `useMarketplaceRequestCount` when provided (component-level tests
   * pin the rendering off an explicit count without wiring the API/token
   * mocks the hook needs).
   */
  requestCount?: number;
  /**
   * Count of connections that are held or still charging — the Connections
   * ledger's "Needs attention" tab. Overrides the live count from
   * `useMarketplaceConnectionAttentionCount` when provided.
   */
  attentionCount?: number;
}

function Badge({ testId, count }: { testId: string; count: number }) {
  return (
    <span
      data-testid={testId}
      className="rounded-full bg-pro px-1.5 py-px text-[10px] font-bold text-white"
    >
      {count}
    </span>
  );
}

export function MarketplaceSidebar({
  requestCount,
  attentionCount,
}: MarketplaceSidebarProps = {}) {
  const { pathname } = useLocation();
  const liveRequestCount = useMarketplaceRequestCount();
  const liveAttentionCount = useMarketplaceConnectionAttentionCount();
  const requestBadge = requestCount ?? liveRequestCount;
  const attentionBadge = attentionCount ?? liveAttentionCount;
  // Status chips come from the shared setup store (the same read the Profile /
  // Standing-offers headers use), so an availability change anywhere updates
  // them. Absent while loading, in preview (the store never fetches there), or
  // when no read has succeeded — a secondary affordance never errors the nav.
  const { data: setup } = useMarketplaceSetup();

  const renderDestination = (dest: MarketplaceSidebarDestination) => {
    const Icon = dest.icon;
    if (dest.disabled) {
      // Non-navigable placeholder — visible so the IA is legible, but
      // never a clickable link to a blank/404 route.
      return (
        <div
          key={dest.id}
          aria-disabled="true"
          title="Coming soon"
          className="flex cursor-not-allowed select-none items-center gap-2.5 rounded-xl px-3 py-2.5 text-sm text-fg-muted opacity-45"
        >
          <Icon className="size-4 shrink-0" />
          <span className="flex-1">{dest.label}</span>
        </div>
      );
    }

    const prefixes = dest.matchPrefixes ?? [dest.to];
    const isActive = prefixes.some(
      (prefix) => pathname === prefix || pathname.startsWith(`${prefix}/`),
    );

    return (
      <NavLink
        key={dest.id}
        to={dest.to}
        className={[
          "flex items-center gap-2.5 rounded-xl px-3 py-2.5 text-sm no-underline outline-none",
          "transition-[background-color,box-shadow,color] duration-150",
          "focus-visible:ring-2 focus-visible:ring-pro/30",
          isActive
            ? "bg-white font-semibold text-fg shadow-[0_1px_3px_rgba(15,23,42,0.10)] ring-1 ring-border/60"
            : "font-medium text-fg-muted hover:bg-white/60 hover:text-fg",
        ].join(" ")}
        aria-current={isActive ? "page" : undefined}
      >
        <Icon className="size-4 shrink-0" />
        <span className="flex-1">{dest.label}</span>
        {dest.id === "marketplace-requests" && requestBadge > 0 ? (
          <Badge testId="marketplace-requests-badge" count={requestBadge} />
        ) : null}
        {dest.id === "marketplace-connections" && attentionBadge > 0 ? (
          <Badge testId="marketplace-connections-badge" count={attentionBadge} />
        ) : null}
      </NavLink>
    );
  };

  const badge = setup ? deliveryBadge(setup) : null;
  const pill = setup ? availabilityPill(setup) : null;

  return (
    <nav aria-label="Marketplace navigation" className="flex flex-col gap-4 px-3 py-5">
      <div className="flex flex-col gap-2 px-2">
        <div className="text-sm font-semibold text-fg">Marketplace</div>
        {badge && pill ? (
          <div
            data-testid="marketplace-sidebar-status"
            className="flex flex-wrap items-center gap-1.5"
          >
            <span
              className={`inline-flex items-center gap-1.5 rounded-full border px-2.5 py-1 text-[11px] font-semibold ${badge.className}`}
            >
              <badge.icon className="size-3" />
              {badge.label}
            </span>
            <span
              className={`inline-flex items-center gap-1.5 rounded-full border px-2.5 py-1 text-[11px] font-semibold ${pill.className}`}
            >
              <span
                aria-hidden="true"
                className={`size-1.5 rounded-full ${pill.dotClassName}`}
              />
              {pill.label}
            </span>
          </div>
        ) : null}
      </div>

      {MARKETPLACE_SIDEBAR_GROUPS.map((group) => (
        <div
          key={group.id}
          role="group"
          aria-label={group.label}
          className="flex flex-col gap-1.5"
        >
          <div className="px-2 text-[11px] font-bold tracking-[0.2em] text-fg-muted uppercase">
            {group.label}
          </div>
          {group.destinations.map(renderDestination)}
        </div>
      ))}
    </nav>
  );
}
